import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Badge, Box, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import CampaignService from "@/services/agency/CampaignService";
import { InfoField, JsonSection, SectionCard, WorkflowStatusBadge } from "@/components/ui/detail/primitives";
import { SCORING_ENABLED } from "@/scoringUi";

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("pl-PL") : "—");

const CampaignDetailPage = () => {
    const { campaignId } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        setNotFound(false);
        CampaignService.getById(campaignId)
            .then((data) => (data?.id ? setCampaign(data) : setNotFound(true)))
            .catch(() => setNotFound(true))
            .finally(() => setIsLoading(false));
    }, [campaignId]);

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="brand.400" size="lg" />
            </VStack>
        );
    }

    if (notFound || !campaign) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="text.grey">Nie znaleziono kampanii.</Text>
            </VStack>
        );
    }

    return (
        <VStack w="full" py={6} gap={6} align="flex-start">
            <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                <HStack justify="space-between" align="flex-start" mb={4} flexWrap="wrap" gap={3}>
                    <VStack align="flex-start" gap={1}>
                        <Text fontWeight={700} fontSize="xl" color="white">{campaign.name}</Text>
                        <Text color="text.grey" fontSize="xs" fontFamily="mono">{campaign.id}</Text>
                    </VStack>
                    <HStack gap={2}>
                        {campaign.isFavorite && <Badge colorPalette="yellow" variant="subtle">Ulubiona</Badge>}
                        <WorkflowStatusBadge status={campaign.status} />
                    </HStack>
                </HStack>
                <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={5}>
                    <InfoField label="Nazwa agenta">{campaign.agentDisplayName || "—"}</InfoField>
                    <InfoField label="Języki">{(campaign.languages ?? []).join(", ") || "—"}</InfoField>
                    <InfoField label="Scenariusz">{campaign.scenario?.name || "—"}</InfoField>
                    <InfoField label="Źródło leadów">{campaign.leadSourceType || "—"}</InfoField>
                    <InfoField label="Powiadomienia">{campaign.sendNotifications ? (campaign.notificationEmails || "tak") : "wyłączone"}</InfoField>
                    <InfoField label="Utworzono">{fmtDateTime(campaign.createdAtUtc)}</InfoField>
                    <InfoField label="Zaktualizowano">{fmtDateTime(campaign.updatedAtUtc)}</InfoField>
                </Box>
                {campaign.description && (
                    <Box mt={4}>
                        <InfoField label="Opis"><Text color="white" fontSize="sm">{campaign.description}</Text></InfoField>
                    </Box>
                )}
            </Box>

            {campaign.companyInfo && (
                <JsonSection title="Informacje o firmie" data={campaign.companyInfo} downloadFilename={`campaign_company_${campaignId}.json`} />
            )}
            <JsonSection title="Produkty i usługi" data={campaign.products} downloadFilename={`campaign_products_${campaignId}.json`} />
            <JsonSection title="Argumenty i obiekcje" data={{ salesArguments: campaign.salesArguments, objections: campaign.objections }} downloadFilename={`campaign_sales_${campaignId}.json`} />
            <JsonSection title="Cele konwersji" data={campaign.conversionGoals} downloadFilename={`campaign_goals_${campaignId}.json`} />
            <JsonSection title="Kwalifikacja" data={{ qualifyingQuestions: campaign.qualifyingQuestions, qualificationCriteria: campaign.qualificationCriteria, disqualificationAction: campaign.disqualificationAction, disqualificationActionDetail: campaign.disqualificationActionDetail }} downloadFilename={`campaign_qualification_${campaignId}.json`} />
            <JsonSection title="Kanały komunikacji" data={campaign.communicationChannels} downloadFilename={`campaign_channels_${campaignId}.json`} />
            <JsonSection title="Automatyzacja statusów" data={campaign.statusAutomationRules} downloadFilename={`campaign_status_rules_${campaignId}.json`} />
            <JsonSection title="Follow-up" data={campaign.followUpConfig} downloadFilename={`campaign_followup_${campaignId}.json`} />
            <JsonSection title="Eskalacja" data={campaign.escalationConfig} downloadFilename={`campaign_escalation_${campaignId}.json`} />
            <JsonSection title="Styl rozmowy" data={campaign.conversationStyle} downloadFilename={`campaign_style_${campaignId}.json`} />
            <JsonSection title="Źródło leadów — konfiguracja i mapowanie" data={{ leadSourceConfig: campaign.leadSourceConfig, leadFieldMappings: campaign.leadFieldMappings, customFieldMappings: campaign.customFieldMappings }} downloadFilename={`campaign_lead_source_${campaignId}.json`} />
            <JsonSection title="Integracje wyjściowe (output)" data={campaign.outputIntegrations} downloadFilename={`campaign_output_${campaignId}.json`} />
            {SCORING_ENABLED && (
                <JsonSection title="Scoring (nadpisania / kryteria lokalne)" data={{ scoringOverrides: campaign.scoringOverrides, localCriteria: campaign.localCriteria }} downloadFilename={`campaign_scoring_${campaignId}.json`} />
            )}
        </VStack>
    );
};

export default CampaignDetailPage;
